/**
 * Property Fit / Suitability Engine
 *
 * Deterministic, explainable scoring based entirely on existing property data.
 * No ML, no external APIs, no black-box logic.
 * Every tag and score maps directly to a visible property field.
 */

const COM_TYPES = ["office", "shop", "showroom", "warehouse", "clinic", "coworking", "industrial"];
const isCommercial = (pt) => COM_TYPES.includes(pt);

/**
 * Compute property fit result.
 * @param {Object} property - The property object (addr, rooms, rent, amen, etc.)
 * @param {Object} opts - { leases, role, t, h }
 *   leases: array of lease objects
 *   role: "landlord" | "tenant"
 *   t: translation object
 *   h: theme object
 * @returns {Object} { label, labelKey, score, color, tags, strengths, cautions, category }
 */
function getPropertyFit(property, { leases = [], role = "tenant", t = {}, h = {} } = {}) {
  const p = property;
  if (!p) return null;

  const pt = p.property_type || "apartment";
  const commercial = isCommercial(pt);
  const category = commercial ? "commercial" : "residential";

  const amen = Array.isArray(p.amen) ? p.amen : [];
  const amenStr = (Array.isArray(p.amen) ? p.amen.join(" ") : (typeof p.amen === "string" ? p.amen : "")).toLowerCase();
  const hasParking = !!(p.parking && p.parking.trim()) || amenStr.includes("parking") || amenStr.includes("car") || amenStr.includes("bike");
  const hasArea = !!(p.area_sqft && p.area_sqft > 0);
  const isVacant = !p.occ;

  // Find active lease for this property
  const activeLease = leases.find(l =>
    l.property_id === p.id && (l.status === "active" || l.status === "draft")
  );

  let score = 0;
  const tags = [];
  const strengths = [];
  const cautions = [];

  if (!commercial) {
    // --- RESIDENTIAL SCORING ---

    // Furnishing (0/10/20)
    const furn = p.furnishing || "unfurnished";
    if (furn === "furnished") { score += 20; tags.push(t.fitFurnished || "Furnished"); strengths.push(t.fitFurnishedDesc || "Fully furnished — move-in ready"); }
    else if (furn === "semi") { score += 10; tags.push(t.fitSemiFurnished || "Semi-Furnished"); strengths.push(t.fitSemiFurnishedDesc || "Semi-furnished with basic fittings"); }
    else { cautions.push(t.fitUnfurnishedDesc || "Unfurnished — you may need to furnish"); }

    // Parking (0/15)
    if (hasParking) { score += 15; tags.push(t.fitParkingAvail || "Parking"); strengths.push(t.fitParkingDesc || "Parking available"); }
    else { cautions.push(t.fitNoParkingDesc || "No parking information provided"); }

    // Amenity richness (0-20)
    const ac = amen.length;
    if (ac >= 7) { score += 20; tags.push(t.fitWellEquipped || "Well-Equipped"); strengths.push(t.fitWellEquippedDesc || "Well-equipped with " + ac + " amenities"); }
    else if (ac >= 5) { score += 15; tags.push(t.fitWellEquipped || "Well-Equipped"); strengths.push(t.fitWellEquippedDesc || "Well-equipped with " + ac + " amenities"); }
    else if (ac >= 3) { score += 10; }
    else if (ac >= 1) { score += 5; }
    if (ac <= 1) { cautions.push(t.fitLimitedAmenitiesDesc || "Limited amenities listed"); }

    // Room sizing (0-10)
    const rooms = p.rooms || 0;
    if (rooms === 1) { score += 5; tags.push(t.fitCompactLiving || "Compact"); }
    else if (rooms >= 2 && rooms <= 3) { score += 10; tags.push(t.fitFamilyHome || "Family Home"); }
    else if (rooms >= 4) { score += 8; tags.push(t.fitSpacious || "Spacious"); }

    // Availability (0/15)
    if (isVacant) { score += 15; tags.push(t.fitAvailableNow || "Available Now"); strengths.push(t.fitAvailableNowDesc || "Currently vacant — available immediately"); }

    // Budget signal (0-10)
    const rentPerRoom = rooms > 0 ? p.rent / rooms : p.rent;
    if (rentPerRoom <= 5000) { score += 10; tags.push(t.fitBudgetFriendly || "Budget-Friendly"); strengths.push(t.fitBudgetDesc || "Affordable rent for this size"); }
    else if (rentPerRoom <= 10000) { score += 7; }
    else if (rentPerRoom <= 20000) { score += 4; }
    else { score += 2; }

    // Lease readiness (0/10)
    if (role === "landlord" && activeLease) { score += 10; tags.push(t.fitLeaseReady || "Lease Active"); }
    else if (role === "tenant" && isVacant && !activeLease) { score += 10; }

  } else {
    // --- COMMERCIAL SCORING ---

    // Area info (0/15)
    if (hasArea) { score += 15; strengths.push(t.fitAreaInfoDesc || "Area documented: " + p.area_sqft + " sq.ft");
      if (p.area_sqft > 1000) { tags.push(t.fitLargeSpace || "Large Space"); }
      else if (p.area_sqft <= 500) { tags.push(t.fitCompactSpace || "Compact Space"); }
    } else { cautions.push(t.fitNoAreaInfoDesc || "Area (sq.ft) not specified"); }

    // Parking (0/15)
    if (hasParking) { score += 15; tags.push(t.fitParkingAvail || "Parking"); strengths.push(t.fitParkingDesc || "Parking available"); }
    else { cautions.push(t.fitNoParkingDesc || "No parking information provided"); }

    // Amenity richness (0-20)
    const ac = amen.length;
    if (ac >= 7) { score += 20; }
    else if (ac >= 5) { score += 15; tags.push(t.fitWellEquipped || "Well-Equipped"); }
    else if (ac >= 3) { score += 10; }
    else if (ac >= 1) { score += 5; }
    if (ac <= 1) { cautions.push(t.fitLimitedAmenitiesDesc || "Limited amenities listed"); }

    // Availability (0/15)
    if (isVacant) { score += 15; tags.push(t.fitAvailableNow || "Available Now"); strengths.push(t.fitAvailableNowDesc || "Currently vacant — available immediately"); }

    // Type clarity (5-15)
    const typeTag = {
      office: t.fitOfficeReady || "Office-Ready",
      shop: t.fitShopReady || "Shop-Ready",
      showroom: t.fitShowroomReady || "Showroom-Ready",
      warehouse: t.fitWarehouseSpace || "Warehouse",
      clinic: t.fitClinicReady || "Clinic-Ready",
      coworking: t.fitCoworkingReady || "Coworking-Ready",
      industrial: t.fitIndustrialSpace || "Industrial"
    };
    if (typeTag[pt]) { score += 15; tags.push(typeTag[pt]); }
    else { score += 5; }

    // Budget signal (0-10)
    if (hasArea && p.area_sqft > 0) {
      const rentPerSqft = p.rent / p.area_sqft;
      if (rentPerSqft <= 20) { score += 10; tags.push(t.fitBudgetFriendly || "Budget-Friendly"); }
      else if (rentPerSqft <= 40) { score += 7; }
      else { score += 4; }
    } else {
      if (p.rent <= 15000) { score += 8; tags.push(t.fitBudgetFriendly || "Budget-Friendly"); }
      else if (p.rent <= 30000) { score += 5; }
      else { score += 3; }
    }

    // Lease readiness (0/10)
    if (role === "landlord" && activeLease) { score += 10; tags.push(t.fitLeaseReady || "Lease Active"); }
    else if (role === "tenant" && isVacant && !activeLease) { score += 10; }
  }

  // Clamp score
  score = Math.min(100, Math.max(0, score));

  // Check for very low data completeness
  if (amen.length === 0 && !hasParking && !hasArea && (!p.furnishing || p.furnishing === "unfurnished")) {
    cautions.push(t.fitLimitedDetailsDesc || "Limited property details available");
  }

  // Determine label tier
  let labelKey, color;
  if (score >= 80) { labelKey = "fitStrong"; color = h.gn || "#30d158"; }
  else if (score >= 60) { labelKey = "fitGood"; color = h.ac || "#0a84ff"; }
  else if (score >= 40) { labelKey = "fitBasic"; color = h.wn || "#ff9f0a"; }
  else { labelKey = "fitReview"; color = h.sc || "#8e8e93"; }

  return {
    label: t[labelKey] || labelKey,
    labelKey,
    score,
    color,
    tags: tags.slice(0, 5), // Cap at 5 most relevant tags
    strengths,
    cautions,
    category
  };
}

export { getPropertyFit, isCommercial, COM_TYPES };
